import { hydrateMemberFromLookup } from "../member/deactivatedMemberOwner";
import { useMembers } from "../member/useMembers";
import { getUserIdsFromStorageZaps } from "./utils/getUserIdsFromStorageZaps";
import { StorageZap } from "./useQueryStorageZaps";
import { UNTITLED_ZAP_TITLE } from "@zapier/utils";
import { useFolderTree } from "../folderTree/useFolderTree";
import { useMemo } from "react";

interface HydrateOptions {
  membershipStatus?: string;
}

export function useHydrateStorageZaps(
  _storageZaps: (StorageZap | undefined)[],
  options?: HydrateOptions,
) {
  // Filter out any undefined Zaps
  const storageZaps = _storageZaps.filter(Boolean) as StorageZap[];

  // Fetch Member objects for all Zap owners
  const ownerIds = getUserIdsFromStorageZaps(storageZaps);
  const { members, isPending: isMembersPending = false } = useMembers({
    ids: ownerIds,
    membershipStatus: options?.membershipStatus,
  });

  const { hash: folderHash } = useFolderTree(
    {},
    { enabled: storageZaps.length > 0 },
  );

  const list = useMemo(() => {
    return storageZaps.map((zap) => {
      const owner = zap.customuserId
        ? hydrateMemberFromLookup(members, zap.customuserId, {
            deferDeactivatedUntilMembersResolve: true,
            isMembersPending,
          })
        : undefined;
      const folder = zap.folderId && folderHash?.[zap.folderId];

      return {
        ...zap,
        ...(folder ? { folder } : {}),
        ...(owner ? { owner } : {}),
        title: zap.title || UNTITLED_ZAP_TITLE,
      };
    });
  }, [storageZaps, members, folderHash, isMembersPending]);

  const hash = useMemo(() => {
    return list.reduce(
      (accumulator, zap) => {
        accumulator[zap.id] = zap;
        if (zap.legacyNodeId) {
          accumulator[zap.legacyNodeId] = zap;
        }
        return accumulator;
      },
      {} as Record<string, StorageZap>,
    );
  }, [list]);

  return { list, hash };
}
